import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { ExportButton, exportToExcelWithXLSX, SearchInput, useToast, Button, SortableDataTable, } from "@perfectserve/library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronRight, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { normalizeInteractionFinalAction } from "../types/platformMetrics";
import { formatTimeDetailed } from "../utils/formatters";
import { URGENT_PRIORITIES, ROUTINE_PRIORITIES } from "../utils/executiveSummaryUtils";
import InteractionList from "../views/InteractionList";
const ResponseMetrics = ({ interactions, title = "Response Metrics", onInteractionClick: _onInteractionClick, viewSettings, onClose, }) => {
    const { showToast } = useToast();
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRows, setExpandedRows] = useState(new Set());
    const calculateMedian = (values) => {
        if (values.length === 0)
            return 0;
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 0) {
            return ((sorted[mid - 1] || 0) + (sorted[mid] || 0)) / 2;
        }
        return sorted[mid] || 0;
    };
    const getReadTimeValue = (metric) => {
        return viewSettings.showMedianUrgentReadTime ? metric.medianReadTime : metric.avgReadTime;
    };
    const getCallbackTimeValue = (metric) => {
        return viewSettings.showMedianCallbackTime ? metric.medianCallbackTime : metric.avgCallbackTime;
    };
    const getCallbackDurationValue = (metric) => {
        return viewSettings.showMedianCallbackDuration ? metric.medianCallbackDuration : metric.avgCallbackDuration;
    };
    const getReadTimeHeader = () => {
        return viewSettings.showMedianUrgentReadTime ? "Median Read Time" : "Avg Read Time";
    };
    const getCallbackTimeHeader = () => {
        return viewSettings.showMedianCallbackTime ? "Median Callback Time" : "Avg Callback Time";
    };
    const getCallbackDurationHeader = () => {
        return viewSettings.showMedianCallbackDuration ? "Median Callback Duration" : "Avg Callback Duration";
    };
    const toggleRowExpansion = (recipientId) => {
        const id = String(recipientId);
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(id)) {
            newExpandedRows.delete(id);
        }
        else {
            newExpandedRows.add(id);
        }
        setExpandedRows(newExpandedRows);
    };
    const handleExcelExport = async () => {
        try {
            const exportData = recipientMetrics.map((metric) => ({
                Recipient: metric.toObjectName,
                Requested: metric.requested,
                "Secure Messages": metric.secureMessages,
                "Voice Messages": metric.voiceMessages,
                Urgent: metric.urgent,
                Routine: metric.routine,
                "Avg Read Time": formatTimeDetailed(metric.avgReadTime),
                "Median Read Time": formatTimeDetailed(metric.medianReadTime),
                Callbacks: metric.callbackCount,
                "Avg Callback Time": formatTimeDetailed(metric.avgCallbackTime),
                "Median Callback Time": formatTimeDetailed(metric.medianCallbackTime),
                "Read Time Coverage": `${metric.readTimesCoverage}%`,
            }));
            await exportToExcelWithXLSX({
                filename: "Response_Metrics",
                data: exportData,
                sheetName: "Response Metrics",
            });
        }
        catch (error) {
            console.error("Excel export failed:", error);
            showToast({
                message: "Excel export failed. Please try again.",
                type: "error",
                position: "top-right",
            });
        }
    };
    const recipientMetrics = useMemo(() => {
        const recipientGroups = {};
        interactions.forEach((interaction) => {
            if (interaction.ToObjectId) {
                const objectId = interaction.ToObjectId;
                if (!recipientGroups[objectId]) {
                    recipientGroups[objectId] = [];
                }
                recipientGroups[objectId].push(interaction);
            }
        });
        const metrics = Object.entries(recipientGroups).map(([toObjectId, recipientInteractions]) => {
            var _a;
            const toObjectName = ((_a = recipientInteractions[0]) === null || _a === void 0 ? void 0 : _a.ToObjectName) || "Unknown";
            const requested = interactions.filter((interaction) => {
                if (!interaction.ToFilterRequestedObject || !toObjectName) {
                    return false;
                }
                const requestedObject = interaction.ToFilterRequestedObject.trim();
                const objectName = toObjectName.trim();
                return requestedObject.includes(objectName) || objectName.includes(requestedObject);
            }).length;
            const secureMessages = recipientInteractions.filter((interaction) => {
                const normalizedAction = normalizeInteractionFinalAction(interaction.InteractionFinalAction);
                return normalizedAction === "Agent Text" || normalizedAction === "AgentText";
            }).length;
            const voiceMessages = recipientInteractions.filter((interaction) => normalizeInteractionFinalAction(interaction.InteractionFinalAction) === "VoiceMessage").length;
            const secureMessageInteractions = recipientInteractions.filter((interaction) => {
                const normalizedAction = normalizeInteractionFinalAction(interaction.InteractionFinalAction);
                return normalizedAction === "Agent Text" || normalizedAction === "AgentText";
            });
            const urgent = secureMessageInteractions.filter((interaction) => URGENT_PRIORITIES.includes(interaction.ToFilterPriority || "")).length;
            const routine = secureMessageInteractions.filter((interaction) => ROUTINE_PRIORITIES.includes(interaction.ToFilterPriority || "")).length;
            const readTimes = secureMessageInteractions
                .filter((interaction) => interaction.ReadInSeconds && interaction.ReadInSeconds > 0)
                .map((interaction) => interaction.ReadInSeconds);
            const avgReadTime = readTimes.length > 0 ? readTimes.reduce((sum, time) => sum + time, 0) / readTimes.length : 0;
            const medianReadTime = calculateMedian(readTimes);
            const readTimesCoverage = secureMessages > 0 ? (readTimes.length / secureMessages) * 100 : 0;
            const hasGoodReadTimeCoverage = readTimesCoverage >= 20;
            const callbackTimes = recipientInteractions
                .filter((interaction) => interaction.CallbackInSeconds && interaction.CallbackInSeconds > 0)
                .map((interaction) => interaction.CallbackInSeconds);
            const avgCallbackTime = callbackTimes.length > 0 ? callbackTimes.reduce((sum, time) => sum + time, 0) / callbackTimes.length : 0;
            const medianCallbackTime = calculateMedian(callbackTimes);
            const callbackCount = recipientInteractions
                .filter((interaction) => interaction.CallbackFromMessageIdCount && interaction.CallbackFromMessageIdCount > 0)
                .reduce((sum, interaction) => sum + (interaction.CallbackFromMessageIdCount || 0), 0);
            const callbackDurations = recipientInteractions
                .filter((interaction) => interaction.CallbackDurationSeconds && interaction.CallbackDurationSeconds > 0)
                .map((interaction) => interaction.CallbackDurationSeconds);
            const avgCallbackDuration = callbackDurations.length > 0
                ? callbackDurations.reduce((sum, duration) => sum + duration, 0) / callbackDurations.length
                : 0;
            const medianCallbackDuration = calculateMedian(callbackDurations);
            return {
                toObjectId,
                toObjectName,
                requested,
                secureMessages,
                voiceMessages,
                urgent,
                routine,
                avgReadTime,
                medianReadTime,
                avgCallbackTime,
                medianCallbackTime,
                callbackCount,
                avgCallbackDuration,
                medianCallbackDuration,
                readTimesCoverage,
                hasGoodReadTimeCoverage,
                recipientInteractions,
            };
        });
        return metrics.sort((a, b) => a.toObjectName.localeCompare(b.toObjectName));
    }, [interactions]);
    const { frequentUsers, lessFrequentUsers } = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();
        const filterBySearch = (metric) => !searchTerm.trim() ||
            metric.toObjectName.toLowerCase().includes(searchLower) ||
            metric.toObjectId.toLowerCase().includes(searchLower);
        const frequent = recipientMetrics.filter((metric) => metric.hasGoodReadTimeCoverage).filter(filterBySearch);
        const lessFrequent = recipientMetrics.filter((metric) => !metric.hasGoodReadTimeCoverage).filter(filterBySearch);
        return {
            frequentUsers: frequent,
            lessFrequentUsers: lessFrequent,
        };
    }, [recipientMetrics, searchTerm]);
    const columns = useMemo(() => [
        {
            key: "expand",
            label: "",
            sortable: false,
            align: "center",
            width: "40px",
            render: (_, row) => (_jsx("button", { onClick: () => toggleRowExpansion(row.toObjectId), className: "ps-sortable-table-action-btn", title: `${expandedRows.has(row.toObjectId) ? "Collapse" : "Expand"} interactions for ${row.toObjectName}`, children: _jsx(FontAwesomeIcon, { icon: expandedRows.has(row.toObjectId) ? faChevronDown : faChevronRight }) })),
        },
        {
            key: "toObjectName",
            label: "Recipient",
            sortable: true,
            align: "left",
            width: "200px",
            className: "font-weight-500",
            render: (value) => _jsx("div", { style: { fontSize: "14px" }, children: value }),
        },
        {
            key: "requested",
            label: "Requested",
            sortable: true,
            align: "center",
            width: "80px",
        },
        {
            key: "secureMessages",
            label: "Secure Messages",
            sortable: true,
            align: "center",
            width: "100px",
        },
        {
            key: "voiceMessages",
            label: "Voice Messages",
            sortable: true,
            align: "center",
            width: "100px",
        },
        {
            key: "urgent",
            label: "Urgent",
            sortable: true,
            align: "center",
            width: "80px",
        },
        {
            key: "routine",
            label: "Routine",
            sortable: true,
            align: "center",
            width: "80px",
        },
        {
            key: viewSettings.showMedianUrgentReadTime ? "medianReadTime" : "avgReadTime",
            label: getReadTimeHeader(),
            sortable: true,
            align: "center",
            width: "120px",
            render: (_, row) => formatTimeDetailed(getReadTimeValue(row)),
        },
        {
            key: viewSettings.showMedianCallbackTime ? "medianCallbackTime" : "avgCallbackTime",
            label: getCallbackTimeHeader(),
            sortable: true,
            align: "center",
            width: "120px",
            render: (_, row) => formatTimeDetailed(getCallbackTimeValue(row)),
        },
        {
            key: "callbackCount",
            label: "Callback Count",
            sortable: true,
            align: "center",
            width: "100px",
        },
        {
            key: viewSettings.showMedianCallbackDuration ? "medianCallbackDuration" : "avgCallbackDuration",
            label: getCallbackDurationHeader(),
            sortable: true,
            align: "center",
            width: "120px",
            render: (_, row) => formatTimeDetailed(getCallbackDurationValue(row)),
        },
    ], [expandedRows, viewSettings]);
    const totalFiltered = frequentUsers.length + lessFrequentUsers.length;
    const totalRecipients = recipientMetrics.length;
    return (_jsxs("div", { className: "interactions-container", style: { minHeight: "100vh" }, children: [_jsx("div", { className: "interactions-header", children: _jsxs("div", { className: "interactions-controls d-flex justify-content-between align-items-center w-100", children: [onClose && (_jsx(Button, { onClick: onClose, icon: faArrowLeft, iconPosition: "left", children: "Back to Dashboard" })), _jsxs("div", { className: "bold text-center flex-1", children: ["Response Metrics - ", title] }), _jsx(ExportButton, { title: "Response Metrics", onExcelExport: handleExcelExport, exportElementId: "response-metrics-content", variant: "links", buttonStyle: { fontSize: "12px" }, hidePdfExport: true })] }) }), _jsxs("div", { className: "bg-white border border-gray-300 rounded-8 shadow-sm mx-20 mb-20", style: { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }, children: [_jsx("div", { className: "p-20 pb-15", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsx(SearchInput, { value: searchTerm, onChange: setSearchTerm, placeholder: "Search recipients...", width: "300px" }), _jsxs("div", { className: "ps-blue", style: { fontSize: "14px", fontWeight: "500" }, children: [totalFiltered, " recipients (", totalRecipients, " total) \u2022 ", frequentUsers.length, " frequent,", " ", lessFrequentUsers.length, " less frequent"] })] }) }), _jsx("div", { id: "response-metrics-content", className: "p-20 pt-0", children: totalFiltered === 0 ? (_jsx("div", { className: "text-center text-muted p-40", style: { fontSize: "16px" }, children: searchTerm ? "No recipients found matching your search" : "No recipients found with response data" })) : (_jsxs("div", { className: "d-flex flex-column", style: { gap: "24px" }, children: [frequentUsers.length > 0 && (_jsx(SortableDataTable, { columns: columns, data: frequentUsers, defaultSortKey: "toObjectName", defaultSortDirection: "asc", expandableRows: true, expandedRows: expandedRows, onToggleExpanded: toggleRowExpansion, getRowId: (row) => row.toObjectId, renderExpandedRow: (row) => (_jsx(InteractionList, { interactions: row.recipientInteractions, loading: false, title: `${row.toObjectName} - Interactions` })), emptyMessage: "No frequent users found" })), lessFrequentUsers.length > 0 && (_jsx(SortableDataTable, { columns: columns, data: lessFrequentUsers, defaultSortKey: "toObjectName", defaultSortDirection: "asc", expandableRows: true, expandedRows: expandedRows, onToggleExpanded: toggleRowExpansion, getRowId: (row) => row.toObjectId, renderExpandedRow: (row) => (_jsx(InteractionList, { interactions: row.recipientInteractions, loading: false, title: `${row.toObjectName} - Interactions` })), sectionHeader: "Less Frequent Users (<20% read time coverage)", dimmed: true, emptyMessage: "No less frequent users found" }))] })) })] })] }));
};
export default ResponseMetrics;
//# sourceMappingURL=ResponseMetrics.js.map